import React, { useEffect, useState } from "react";
import styled from "styled-components";
import "./v2styles.css";
import { useLoading } from "../../components/LoaderProvider";
import SEO from "../../components/SEO";
import { contactSEO } from "../../data/seo";
import BreadcrumbSchema from "../../components/BreadcrumbSchema";

const ContactPageContainer = styled.div`
  background-image: url("/Marble-light.jpeg");
  background-size: 150%;
  background-position: 30% 70%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
`;

const ContactFormWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 60px 50px;
  max-width: 600px;
  width: 100%;
  position: relative;
  margin-top: 40px;

  &::before {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #515398, #aecdc2);
    border-radius: 14px;
    z-index: -1;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  font-size: 45px;
  color: var(--elegant-black);
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 40px;
  font-size: 18px;
  color: #666;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 30px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--elegant-black);
  font-size: 16px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  font-family: "Figtree", sans-serif;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #515398;
    box-shadow: 0 0 0 3px rgba(81, 83, 152, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  font-family: "Figtree", sans-serif;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #515398;
    box-shadow: 0 0 0 3px rgba(81, 83, 152, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #515398;
  color: white;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 1.2px;
  padding: 18px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Figtree", sans-serif;
  margin-top: 20px;

  &:hover {
    background-color: #3e3e7a;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(81, 83, 152, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SageSeparator = styled.div`
  width: 108px;
  height: 3px;
  border-top: 3px solid #aecdc2;
  margin: 0 auto 40px;
`;

const ValidationError = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
`;

function ScheduleACallPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const { setIsLoading } = useLoading();

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(false);
  }, [setIsLoading]);

  const validateContactInfo = () => {
    const hasEmail = email.trim() !== "";
    const hasPhone = phoneNumber.trim() !== "";
    
    if (!hasEmail && !hasPhone) {
      setValidationError("Please provide either an email address or phone number so we can contact you.");
      return false;
    }
    
    setValidationError("");
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateContactInfo()) {
      return;
    }
    
    // If validation passes, submit the form
    e.target.submit();
  };

  return (
    <>
      <SEO {...contactSEO} />
      <BreadcrumbSchema currentPage="Contact" path="/contact" />
      <div className="prototype">
      <ContactPageContainer>
        <ContactFormWrapper>
          <Title>Schedule a Call</Title>
          <SageSeparator />
          <Subtitle>
            Ready to start your journey to better health? Let's connect and
            discuss how our specialized manual therapy approach can help you
            achieve your wellness goals.
          </Subtitle>

          {validationError && (
            <ValidationError>
              {validationError}
            </ValidationError>
          )}

          <form name="contact" method="POST" netlify onSubmit={handleSubmit}>
            <input
              type="hidden"
              name="subject"
              value={"Contact Request from " + name}
            />
            <input type="hidden" name="form-name" value="contact" />

            <FormGroup>
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormInput
                id="name"
                type="text"
                placeholder="Enter your full name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="phoneNumber">Phone Number</FormLabel>
              <FormInput
                id="phoneNumber"
                type="tel"
                placeholder="Enter your phone number"
                name="phoneNumber"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (validationError && e.target.value.trim() !== "") {
                    setValidationError("");
                  }
                }}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormInput
                id="email"
                type="email"
                placeholder="Enter your email address"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (validationError && e.target.value.trim() !== "") {
                    setValidationError("");
                  }
                }}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea
                id="message"
                placeholder="Tell us about your goals, concerns, or any questions you have..."
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </FormGroup>

            <SubmitButton type="submit">Send Message</SubmitButton>
          </form>
        </ContactFormWrapper>
      </ContactPageContainer>
    </div>
    </>
  );
}

export default ScheduleACallPage;
